import { StyleSheet, View } from 'react-native';

import { Button, IconOfferOutline, Text, tokens } from '@fhs/ui';
import { StyledBlockRenderer } from '@fhs-legacy/frontend/src/pages/loyalty/loyalty-offers/loyalty-offers.styled.default';
import {
  actions,
  selectors,
  useAppDispatch,
  useAppSelector,
} from '@fhs-legacy/frontend/src/state/global-state';
import { CART_REMOVE_OFFER_MODAL } from '@fhs-legacy/frontend/src/utils/test-ids';

interface Props {
  id: string;
  onClose(): void;
}

export function CartActionRemoveOffer({ id, onClose }: Props) {
  id; // TODO: SHOULD BE USED FOR LOGGING PURPOSES
  const selectedOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);

  const dispatch = useAppDispatch();
  const isReward = selectedOffer?.incentives?.find(
    // explicit any because of incentiveType unknown
    (incentive: any) => incentive?.incentiveType?.toLowerCase() === 'reward'
  );

  const handleRemoveOffer = () => {
    dispatch(actions.loyalty.setSelectedOffer(null));
    dispatch(actions.loyalty.resetAppliedOffers());
    // forcing offer re-fetch to have the most updated list of them on the loyalty offers page since the validation overwrites the user offers
    dispatch(actions.loyalty.setShouldRefetchOffers(true));
    onClose();
  };

  return (
    <View
      testID={CART_REMOVE_OFFER_MODAL}
      style={styles.container}
      accessibilityRole="alert"
      accessibilityLabel="Remove incentive confirmation"
    >
      <Text.Heading type="one" accessibilityRole="header">
        Remove {isReward ? 'Reward' : 'Offer'}
      </Text.Heading>
      <Text.Ui size="md" style={styles.copy}>
        Are you sure you want to remove this {isReward ? 'reward' : 'offer'} from your cart?
      </Text.Ui>

      {selectedOffer && (
        <View
          style={styles.offerContainer}
          accessibilityLabel={`Incentive to remove: ${selectedOffer.name?.localeRaw || 'current incentive'}`}
        >
          <IconOfferOutline size={24} accessibilityRole="none" accessibilityElementsHidden />
          <Text.Paragraph size="md" style={styles.offerText}>
            <StyledBlockRenderer content={selectedOffer.name?.localeRaw} />
          </Text.Paragraph>
        </View>
      )}

      <View style={styles.buttonGroup}>
        <Button
          size="lg"
          onPress={handleRemoveOffer}
          accessibilityRole="button"
          accessibilityLabel="Confirm incentive removal"
        >
          <Button.Text>Remove</Button.Text>
        </Button>
        <Button
          size="lg"
          type="outline"
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Cancel incentive removal"
        >
          <Button.Text>Cancel</Button.Text>
        </Button>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  offerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    gap: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: tokens.colors.$black10,
    minHeight: 80,
    width: '100%',
    marginBottom: 20,
  },
  offerText: {
    flex: 1,
    textAlignVertical: 'center',
  },
  copy: {
    paddingTop: 12,
    paddingBottom: 24,
  },
  buttonGroup: {
    gap: 8,
  },
  container: {
    maxWidth: 334,
    marginHorizontal: 'auto',
    marginTop: 20,
    paddingBottom: 33,
  },
});
